// File: frontend/src/services/matchService.ts
import { MergedTopicError } from "./apiClient";
import { MatchModel } from "../models";
import { CountryCountModel } from "../models/CountryCountModel";
import { MATCHES_PER_PAGE , API_BASE_URL } from "../constants";
import { MatchGenderEnum } from "../models/enums/MatchGenderEnum";
import { MatchStatusEnum } from "../models/enums/MatchStatusEnum";
import { SportTypeEnum } from "../models/enums/SportTypeEnum";
import i18n from "../i18n";

interface MatchPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

interface MatchResponse {
  matches: MatchModel[];
  pagination: MatchPagination;
}

export const fetchAllMatches = async (
  page: number = 1,
  showRookieEntries: boolean = true
): Promise<MatchResponse> => {
  const baseUrl = `${API_BASE_URL}/api/matches`;
  const url = `${baseUrl}?page=${page - 1}&size=${MATCHES_PER_PAGE}&showRookieEntries=${showRookieEntries}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_all_matches_failed"));
  }

  return parseResponse(await response.json());
};

export const fetchMatchesByDate = async (
  date: Date,
  page: number = 1,
  showRookieEntries: boolean = true
): Promise<MatchResponse> => {
  const baseUrl = `${API_BASE_URL}/api/matches/date`;
  const dateStr = date.toISOString().split("T")[0];
  const url = `${baseUrl}?date=${dateStr}&page=${page - 1}&size=${MATCHES_PER_PAGE}&showRookieEntries=${showRookieEntries}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_matches_by_date_failed"));
  }

  return parseResponse(await response.json());
};

export const fetchMatchesByDateRange = async (
  startDate: Date,
  endDate: Date,
  page: number = 1,
  showRookieEntries: boolean = true
): Promise<MatchResponse> => {
  const baseUrl = `${API_BASE_URL}/api/matches/date-range`;
  const startStr = startDate.toISOString().split("T")[0];
  const endStr = endDate.toISOString().split("T")[0];
  const url = `${baseUrl}?startDate=${startStr}&endDate=${endStr}&page=${page - 1}&size=${MATCHES_PER_PAGE}&showRookieEntries=${showRookieEntries}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_matches_by_date_range_failed"));
  }

  return parseResponse(await response.json());
};

/**
 * Fetch one page of the left-pane match list.
 *
 * The date window is sent as two UTC instants (startDateTime / endDateTime,
 * "YYYY-MM-DDTHH:mm:ss") rather than calendar dates: a match is bucketed into the
 * day it is played in the VIEWER's timezone, and only an instant comparison can
 * express that. The backend applies the window in SQL, so the page returned here
 * is final — the caller must not filter it again, otherwise the pagination totals
 * stop matching the rows on screen.
 */
export const fetchMatchesByFilter = async (
  sportTypes: SportTypeEnum[] = [],
  matchGenders: MatchGenderEnum[] = [],
  startDateTime: string | null = null,
  endDateTime: string | null = null,
  country: string | null = null,
  // "Canlı" toggle of the left pane — narrows the list to matches in progress
  liveOnly: boolean = false,
  page: number = 1,
  showRookieEntries: boolean = true,
  sortBy: string = "lastEntryAt",
  pageSize: number = MATCHES_PER_PAGE
): Promise<MatchResponse> => {
  const baseUrl = `${API_BASE_URL}/api/matches/filter`;
  const params = new URLSearchParams();

  // Send each sportType as separate parameter (Spring handles as List)
  if (sportTypes && sportTypes.length > 0) {
    sportTypes.forEach(st => params.append("sportTypes", st));
  }
  // Send each matchGender as separate parameter (Spring handles as List)
  if (matchGenders && matchGenders.length > 0) {
    matchGenders.forEach(mg => params.append("matchGenders", mg));
  }
  // UTC instants bounding the viewer's local day window — see the doc comment above
  if (startDateTime) {
    params.append("startDateTime", startDateTime);
  }
  if (endDateTime) {
    params.append("endDateTime", endDateTime);
  }
  // Country filter — matches where either side's club belongs to the country
  if (country) {
    params.append("country", country);
  }
  // Only sent when on — an absent param keeps the URL (and the cache key) clean
  if (liveOnly) {
    params.append("liveOnly", "true");
  }
  params.append("page", (page - 1).toString());
  params.append("size", pageSize.toString());
  params.append("showRookieEntries", showRookieEntries.toString());
  params.append("sortBy", sortBy);

  const url = `${baseUrl}?${params.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_matches_by_filter_failed"));
  }

  return parseResponse(await response.json());
};

/**
 * Distinct country codes of the clubs involved in non-deleted matches with
 * per-country match counts. Feeds the left-pane match country filter dropdown.
 * Mirrors clubService.getClubCountries / teamService.getTeamCountries.
 */
export const fetchMatchCountries = async (): Promise<CountryCountModel[]> => {
  const url = `${API_BASE_URL}/api/matches/countries`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_matches_by_filter_failed"));
  }

  return response.json();
};

/**
 * Search matches by team name — used by the (bkz:) reference popup in the entry editor.
 * Returns up to `size` results matching the query against home or away team name.
 */
export const searchMatches = async (q: string, size: number = 8): Promise<MatchModel[]> => {
  const baseUrl = `${API_BASE_URL}/api/matches/search`;
  const url = `${baseUrl}?q=${encodeURIComponent(q)}&size=${size}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Match search failed");
  }

  const data: any[] = await response.json();
  return data.map(mapMatchResponse);
};

/**
 * Fetch a single match by ID.
 * Used when navigating to a match that's not in the current paginated list.
 */
export const fetchMatchById = async (matchId: number): Promise<MatchModel | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/matches/${matchId}`);

    // 410 Gone: match was merged into another match — surface the redirect
    // path so useMatchSelection can navigate to the surviving match page
    if (response.status === 410) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.redirectPath) {
        throw new MergedTopicError(errorData.redirectPath);
      }
      return null;
    }

    if (!response.ok) {
      return null;
    }
    
    const match = await response.json();
    return mapMatchResponse(match);
  } catch (error) {
    // Let merge redirects propagate to the caller (useMatchSelection navigates)
    if (error instanceof MergedTopicError) throw error;
    console.error("Failed to fetch match by ID:", error);
    return null;
  }
};

/**
 * Pick the best display name for a team based on the current locale.
 * Priority: locale-specific name → shortName (original/fallback)
 */
const resolveTeamName = (shortName: string, nameTr: string | null, nameEn: string | null): string => {
  const lang = i18n.language?.startsWith("tr") ? "tr" : "en";
  if (lang === "tr" && nameTr) return nameTr;
  if (lang === "en" && nameEn) return nameEn;
  return shortName;
};

/**
 * Pick the best display name for a club based on the current locale.
 * Mirrors resolveTeamName — exported so ClubRow, ClubPage, StatisticsPage etc. share the same logic.
 * nameEn is optional to accommodate DTOs that do not carry it (e.g. TopClubDto, FanClubStatDto).
 * Priority: locale-specific name → shortName (original/fallback)
 */
export const resolveClubName = (club: {
  shortName: string;
  nameTr: string | null;
  nameEn?: string | null;
}): string => {
  const lang = i18n.language?.startsWith("tr") ? "tr" : "en";
  if (lang === "tr" && club.nameTr) return club.nameTr;
  if (lang === "en" && club.nameEn) return club.nameEn;
  return club.shortName;
};

/**
 * Map backend response to frontend MatchModel
 * Backend uses homeTeamShortName, frontend expects homeTeam for backward compatibility
 */
const mapMatchResponse = (match: any): MatchModel => {
  return {
    id: match.id,
    homeTeamId: match.homeTeamId,
    homeTeam: resolveTeamName(match.homeTeamShortName || "", match.homeTeamNameTr ?? null, match.homeTeamNameEn ?? null),
    homeTeamNameTr: match.homeTeamNameTr ?? null,
    homeTeamNameEn: match.homeTeamNameEn ?? null,
    homeTeamLongName: match.homeTeamLongName || "",
    homeTeamAbbreviation: match.homeTeamAbbreviation || "",
    homeTeamCountry: match.homeTeamCountry || "",
    homeTeamLogoUrl: match.homeTeamLogoUrl || undefined,
    // Used to build the /takim/{clubSlug}/{teamSlug} link in the entry title
    homeTeamClubSlug: match.homeTeamClubSlug || undefined,
    homeTeamCode: match.homeTeamCode ?? null,
    awayTeamId: match.awayTeamId,
    awayTeam: resolveTeamName(match.awayTeamShortName || "", match.awayTeamNameTr ?? null, match.awayTeamNameEn ?? null),
    awayTeamNameTr: match.awayTeamNameTr ?? null,
    awayTeamNameEn: match.awayTeamNameEn ?? null,
    awayTeamLongName: match.awayTeamLongName || "",
    awayTeamAbbreviation: match.awayTeamAbbreviation || "",
    awayTeamCountry: match.awayTeamCountry || "",
    awayTeamLogoUrl: match.awayTeamLogoUrl || undefined,
    // Used to build the /takim/{clubSlug}/{teamSlug} link in the entry title
    awayTeamClubSlug: match.awayTeamClubSlug || undefined,
    awayTeamCode: match.awayTeamCode ?? null,
    homeScore: match.homeScore || 0,
    awayScore: match.awayScore || 0,
    // Extra time and penalty scores — null means not played
    homeScoreEt: match.homeScoreEt ?? null,
    awayScoreEt: match.awayScoreEt ?? null,
    homeScorePen: match.homeScorePen ?? null,
    awayScorePen: match.awayScorePen ?? null,
    overtimePeriods: match.overtimePeriods ?? 0,
    stadiumName: match.stadiumName || "",
    matchDate: new Date(match.matchDate),
    matchTime: match.matchTime || "",
    sportType: match.sportType,
    matchGender: match.matchGender || MatchGenderEnum.MALE,
    league: match.league || undefined,
    season: match.season || undefined,
    round: match.round || undefined,
    status: match.status || MatchStatusEnum.SCHEDULED,
    createdByUsername: match.createdByUsername,
    createdAt: match.createdAt ? new Date(match.createdAt) : new Date(),
    entryCount: match.entryCount ?? 0,
    recentEntryCount: match.recentEntryCount ?? 0,
    lastEntryAt: match.lastEntryAt ? new Date(match.lastEntryAt) : undefined,
    // Permanent slug from the server — use this instead of regenerating from team names
    slug: match.slug ?? undefined,
    // Referee assignments — array of { assignmentId, personId, displayName, personSlug, refereeRole }
    referees: Array.isArray(match.referees) ? match.referees : [],
    // Whether new entry submissions are currently blocked for this match page
    isLocked: match.isLocked ?? false,
    lockReason: match.lockReason ?? null,
  };
};

const parseResponse = (responseJson: any): MatchResponse => {
  if (
    !Array.isArray(responseJson.content) ||
    typeof responseJson.currentPage !== "number" ||
    typeof responseJson.totalPages !== "number" ||
    typeof responseJson.totalItems !== "number"
  ) {
    throw new Error(i18n.t("error_invalid_response_data"));
  }

  return {
    matches: responseJson.content.map(mapMatchResponse),
    pagination: {
      currentPage: responseJson.currentPage + 1, // Convert 0-based to 1-based
      totalPages: responseJson.totalPages,
      totalItems: responseJson.totalItems,
    },
  };
};

// Re-export MatchModel for convenience
export { MatchModel } from "../models";